import React, { Component } from 'react';
import { TransactionData } from '../../domain/stream/transaction/TransactionDomains';
import { query, repository, subscribe } from '@trlogic/client-js';
import AchTransactionsActivityComponent from './detail/AchTransactionsActivityComponent';
import { CaseSettings } from '../../domain/case/CaseDomains';
import CollectionNames from '../../constant/CollectionNames';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { RootState, store } from '../../redux/store';
import { changePageFilter } from '../../redux/actions/PageActions';
import FilterUtil from '../../util/FilterUtil';
import DefaultFilterUtil from '../../util/DefaultFilterUtil';
import DomainConfigurationService from '../../service/DomainConfigurationService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import { ConfirmPopup } from 'primereact/confirmpopup';
import AchService from '../../service/AchService';
import AchTransactionItemToolsMenu from './close/AchTransactionItemToolsMenu';
import _ from 'lodash';
import IntervalManager from '../../util/IntervalManager';
import ComponentName from '../../constant/ComponentName';
import { DefaultFilter, UserSystemAttributes } from '../../domain/rbac/RbacDomains';
import Order = query.Order;
import Filter = query.Filter;
import empty = query.empty;
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import Computation = subscribe.Computation;

class AchFileDetailListViewer extends Component<Props, State> {
  public state: State = new State();
  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly caseSettingsService: DomainConfigurationService = ServiceContainer.getInstance().get(ServiceNames.DOMAIN_CONFIGURATION);
  private readonly achService: AchService = ServiceContainer.getInstance().get(ServiceNames.ACH);
  private readonly intervalManager: IntervalManager = InstanceContainer.get(Injectables.INTERVAL_MANAGER);
  private lastSelectedCount: number = 0;
  private transactionSubscription: subscribe.Subscription | undefined;
  private computation: Computation | undefined;

  public render() {
    const { transactions, loading, total, rows, currentPage, sort, filter, currentCaseSettings, settings, updates } = this.state;
    const combinedFilter = this.getCombinedFilter(filter);
    const updateKey = updates[this.props.fileName] ?? 0;
    return (
      <div className='flex flex-col w-full h-full'>
        <ConfirmPopup />
        <div className='flex justify-between bg-white items-center w-full px-5'>
          <AchTransactionItemToolsMenu
            currentCaseSettings={currentCaseSettings}
            loading={loading}
            caseSettings={settings}
            totalRecord={total}
            onChangeFilter={this.onChangeFilter}
            filter={combinedFilter}
            rows={rows}
            currentPage={currentPage}
            sorts={sort}
            fileName={this.props.fileName}
          />
        </div>
        <div className='px-5 h-full'>
          <AchTransactionsActivityComponent
            transactions={transactions}
            total={total}
            rows={rows}
            openInNewPage
            loading={loading}
            paginationFixed={false}
            sort={sort}
            onPageChange={this.onPageChange}
            currentPage={currentPage}
            update={updateKey}
            onSortChange={this.onSortChange}
            multiple
            selectedAlarmDomain={currentCaseSettings}
            onChangeFilter={this.onChangeFilter}
            fileName={this.props.fileName}
          />
        </div>
      </div>
    );
  }

  public componentDidMount = async () => {
    await this.prepareFilterFields();
    await this.loadAllCaseSettings();
    await this.loadTransactions();
    this.startTracker();
    this.startInterval();
  };

  public componentWillUnmount = () => {
    this.stopInterval();
    this.stopTracker();
  };

  public componentDidUpdate = (prevProps: Readonly<Props>, prevState: Readonly<State>) => {
    const prevSelectedCount = prevProps.component?.selectedComponentIds?.length || 0;
    const currentSelectedCount = this.props.component?.selectedComponentIds?.length || 0;
    if (prevSelectedCount > 0 && currentSelectedCount === 0 && this.lastSelectedCount > 0) {
      this.lastSelectedCount = 0;
      this.incrementCurrent();
      setTimeout(() => {
        this.loadTransactions();
      }, 500);
    } else if (currentSelectedCount > 0) {
      this.lastSelectedCount = currentSelectedCount;
    }

    const prevCaseSettings = prevState.currentCaseSettings;
    const currentCaseSettings = this.state.currentCaseSettings;
    if (prevCaseSettings?.dataSource !== currentCaseSettings?.dataSource) {
      this.resubscribeTracker();
    }
  };

  private prepareFilterFields = async () => {
    try {
      await FilterUtil.getCaseFields(undefined, false);
    } catch (error) {
      console.error('Error loading filter fields:', error);
    }
  };

  private loadAllCaseSettings = async () => {
    try {
      const allSettings = await this.repository.findMany<CaseSettings>(QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).build());
      this.setState({ settings: allSettings });
    } catch (error) {
      console.error('Error loading case settings:', error);
      this.setState({ settings: [] });
    }
  };

  private loadCaseSettingsForTransaction = async (transactionName: string) => {
    try {
      const caseSettings = await this.caseSettingsService.getByChannel(transactionName);
      if (caseSettings) {
        this.setState({ currentCaseSettings: caseSettings }, () => {
          this.resubscribeTracker();
          this.incrementCurrent();
        });
        this.applyDefaultFilter(caseSettings);
        await FilterUtil.getCaseFields(caseSettings.dataSource, true);

        const { page } = store.getState();
        const parsedFilter = FilterUtil.parse(page.filter.filter);
        this.setState({ filter: parsedFilter });
        await this.loadTransactions();
      }
    } catch (error) {
      console.error('Error loading case settings for transaction:', error);
    }
  };

  private applyDefaultFilter = (caseSettings: CaseSettings) => {
    const { attributes, changePageFilter, user } = this.props;
    if (!attributes?.filters) {
      return;
    }

    const { page } = store.getState();
    const defaultFilter: DefaultFilter | undefined = DefaultFilterUtil.find(attributes.filters, {
      component: ComponentName.ACH,
      riskType: user!.activeRiskType,
    });

    if (!defaultFilter) {
      return;
    }

    const newFilterFields = defaultFilter.filter.filter.fields.filter(
      (field: any) => !page.filter.filter.fields.some((pageField: any) => pageField.field == field.field),
    );

    if (newFilterFields.length > 0) {
      page.filter.filter.fields.push(...newFilterFields);
      changePageFilter({
        filter: page.filter.filter,
        items: [],
      });
    }
  };

  private loadTransactions = async () => {
    const { fileName, onLoadTransactions } = this.props;
    const { rows, currentPage, sort, filter } = this.state;
    this.setState({ loading: true });
    try {
      const combinedFilter = this.getCombinedFilter(filter);
      const result = await onLoadTransactions(fileName, rows, currentPage, sort, combinedFilter);
      if (result.data && result.data.length > 0 && !this.state.currentCaseSettings) {
        const firstTransaction = result.data[0];
        const transactionName = firstTransaction.name;
        if (transactionName) {
          await this.loadCaseSettingsForTransaction(transactionName);
        }
      }
      this.setState({
        transactions: result.data,
        total: result.count,
        loading: false,
      });
    } catch (error) {
      console.error('Error loading transactions:', error);
      this.setState({ loading: false });
    }
  };

  private onSortChange = (order: Order[]) => {
    const { rows, currentPage } = this.state;
    this.setState({ sort: order, loading: true }, this.restartInterval);
  };

  private onPageChange = (page: number, rows: number) => {
    const { sort } = this.state;
    this.setState({ currentPage: page, rows: rows, loading: true }, this.restartInterval);
  };

  private onChangeFilter = (filter: Filter) => {
    if (_.isEqual(filter, this.state.filter)) return;
    this.setState({ filter, currentPage: 1, loading: true }, this.restartInterval);
  };

  private getCombinedFilter = (userFilter: Filter): Filter => {
    const { fileName } = this.props;
    return this.achService.buildCombinedFilter(fileName, userFilter);
  };

  private startTracker = () => {
    const { currentCaseSettings } = this.state;

    if (!currentCaseSettings?.dataSource) {
      return;
    }

    const transactionQuery = QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('id', currentCaseSettings.dataSource)).build();
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription]);
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    this.transactionSubscription = undefined;
    await this.computation?.stop();
  };

  private tracker = async () => {
    const { currentCaseSettings } = this.state;

    if (!currentCaseSettings?.dataSource) {
      return;
    }
    const isTransactionReady = this.transactionSubscription?.ready();
    if (isTransactionReady) {
      this.incrementCurrent();
    }
  };

  private resubscribeTracker = async () => {
    if (this.computation == undefined) {
      this.startTracker();
      return;
    }
    this.computation?.onStop(() => {
      this.startTracker();
    });
    await this.stopTracker();
  };

  private startInterval = async () => {
    const { fileName } = this.props;
    this.intervalManager.clearTransactionIntervals();
    if (!fileName) return;
    this.intervalManager.addTransactionInterval(setInterval(async () => await this.onInterval(), 5_000) as any);
    await this.onInterval();
  };

  private restartInterval = async () => {
    this.stopInterval();
    await this.startInterval();
  };

  private stopInterval = () => {};

  private onInterval = async () => {
    const { fileName, onLoadTransactions } = this.props;
    const { rows, currentPage, sort, filter, transactions, loading } = this.state;
    if (!fileName) return;

    try {
      const combinedFilter = this.getCombinedFilter(filter);
      const result = await onLoadTransactions(fileName, rows, currentPage, sort, combinedFilter);
      this.setState({ total: result.count });
      if (result.data) {
        if (result.data.length != transactions.length || !_.isEqual(result.data, transactions) || result.data.length == 0 || loading) {
          this.setState({ loading: false, transactions: result.data, total: result.count }, () => {
            this.incrementCurrent();
          });
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  private incrementCurrent = () => {
    const { updates } = this.state;
    const { fileName } = this.props;
    updates[fileName] = (updates[fileName] ?? 0) + 1;
    this.setState({ updates });
  };
}

class State {
  loading: boolean = true;
  transactions: TransactionData[] = [];
  total: number = 0;
  rows: number = 10;
  currentPage: number = 1;
  sort: Order[] = [];
  filter: Filter = empty();
  currentCaseSettings: CaseSettings | undefined = undefined;
  settings: Pick<CaseSettings, 'id' | 'name' | 'dataSource' | 'keyField'>[] = [];
  updates: Record<string, number> = {};
}

interface Props extends ReduxProps {
  fileName: string;
  onLoadTransactions: (
    fileName: string,
    rows: number,
    currentPage: number,
    sort: Order[],
    filter: Filter,
  ) => Promise<{
    data: TransactionData[];
    count: number;
  }>;
}

const mapStateToProps = (state: RootState) => ({
  component: state.component,
  attributes: state.auth.attributes as UserSystemAttributes,
  user: state.auth.user,
});

const mapDispatchToProps = {
  changePageFilter,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(AchFileDetailListViewer);
